/**
 * Instant local search.
 *
 * DESIGN INTENT
 *   Search must feel like it has already happened. The user's whole workspace
 *   is a few thousand documents, which fits in memory comfortably, so we never
 *   ask the network: we scan locally on every keystroke. To stay well inside a
 *   16 ms frame we narrow candidates with a cheap trigram index first and only
 *   then run the expensive ranked matcher on the survivors.
 *
 * DATA DEFINITIONS
 *
 *   A SearchDoc is { id, kind, title, subtitle, haystack, boost, payload }.
 *   Interpretation: one thing a person can find — a file, a folder, a comment,
 *   a historical change, a project, or a command. `title` is what matches
 *   best (a file's name); `haystack` is the broader text we will also match
 *   (its full path, the comment body, the commit message). `boost` lets a
 *   whole class of document outrank another at equal textual quality.
 *
 *   A Match is { doc, score, spans }.
 *   Interpretation: a document that matched, how well, and WHICH CHARACTERS
 *   matched — spans are what let the UI bold the matched letters, which is the
 *   single detail that makes a palette feel responsive rather than magical.
 *
 *   A Span is [start, end) over `title`.
 */
/* ------------------------------------------------------------------ */
/* Scoring                                                             */
/* ------------------------------------------------------------------ */
/** Score awarded for a character that starts a word ("_C" in "Cup_Cathode"). */
const BONUS_BOUNDARY = 14;
/** Score awarded for a character immediately after a previous match. */
const BONUS_CONSECUTIVE = 10;
/** Score awarded when the match starts at position 0. */
const BONUS_PREFIX = 20;
/** Penalty per character skipped between matches. */
const PENALTY_GAP = 2;
/** Base score per matched character. */
const SCORE_CHAR = 6;
/** True when `c` begins a word: a letter after a separator, or a capital run. */
function isBoundary(text, i) {
    if (i === 0)
        return true;
    const prev = text[i - 1];
    const cur = text[i];
    if (/[^A-Za-z0-9]/.test(prev))
        return true;
    if (/[a-z0-9]/.test(prev) && /[A-Z]/.test(cur))
        return true;
    return false;
}
/**
 * Score `query` against `text` as a fuzzy subsequence, returning the score and
 * the matched character positions, or undefined when `query` is not a
 * subsequence of `text` at all.
 *
 * Greedy-forward with a backtracking improvement pass: we take the first
 * subsequence found, then try to slide each matched character rightwards onto
 * a word boundary, which is what makes "ccs" prefer "Cathode_Cup_Support" over
 * "CathodeCupSomething".
 *
 * fuzzyMatch : String String -> {score, positions} | undefined
 */
export function fuzzyMatch(query, text) {
    if (query.length === 0)
        return { score: 0, positions: [] };
    if (query.length > text.length)
        return undefined;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    // Pass 1: does it match at all, and where, greedily left to right?
    const positions = [];
    let at = 0;
    for (const ch of lowerQuery) {
        const found = lowerText.indexOf(ch, at);
        if (found === -1)
            return undefined;
        positions.push(found);
        at = found + 1;
    }
    // Pass 2: slide each position rightwards if a later occurrence of the same
    // character sits on a word boundary and still leaves room for the rest.
    for (let i = positions.length - 1; i >= 0; i--) {
        const limit = i + 1 < positions.length ? positions[i + 1] - 1 : lowerText.length - 1;
        for (let j = positions[i] + 1; j <= limit; j++) {
            if (lowerText[j] === lowerQuery[i] && isBoundary(text, j)) {
                positions[i] = j;
                break;
            }
        }
    }
    // Pass 3: score the chosen positions.
    let score = 0;
    for (let i = 0; i < positions.length; i++) {
        const p = positions[i];
        score += SCORE_CHAR;
        if (isBoundary(text, p))
            score += BONUS_BOUNDARY;
        if (p === 0)
            score += BONUS_PREFIX;
        if (i > 0) {
            const gap = p - positions[i - 1] - 1;
            score += gap === 0 ? BONUS_CONSECUTIVE : -Math.min(gap * PENALTY_GAP, 12);
        }
    }
    // Prefer shorter texts at equal quality: finding "Roof.PDF" should beat
    // finding the same letters inside a 90-character path.
    score += Math.max(0, 24 - text.length / 4);
    return { score, positions };
}
/** Turn sorted matched positions into contiguous [start, end) spans. */
export function positionsToSpans(positions) {
    if (positions.length === 0)
        return [];
    const spans = [];
    let start = positions[0];
    let prev = positions[0];
    for (let i = 1; i < positions.length; i++) {
        if (positions[i] !== prev + 1) {
            spans.push([start, prev + 1]);
            start = positions[i];
        }
        prev = positions[i];
    }
    spans.push([start, prev + 1]);
    return spans;
}
/* ------------------------------------------------------------------ */
/* Index                                                               */
/* ------------------------------------------------------------------ */
/** Queries shorter than this skip the trigram narrowing and scan everything. */
const MIN_TRIGRAM_QUERY = 3;
/**
 * An inverted trigram index over the corpus.
 *
 * Interpretation: for each three-character sequence, which documents contain
 * it. To answer a query we intersect the posting lists of the query's own
 * trigrams, which typically cuts thousands of documents to a few dozen before
 * the ranked matcher runs. Trigrams are computed over title+haystack, both
 * lowercased, so the index is case-insensitive.
 *
 * NOTE: trigram narrowing only finds CONTIGUOUS substrings, so it would miss
 * genuinely fuzzy queries like "ccs". We therefore only narrow when the query
 * itself is contiguous in some document — see `search` for the fallback.
 */
export class SearchIndex {
    docs = [];
    postings = new Map();
    /** Number of documents in the index. */
    get size() { return this.docs.length; }
    /** All documents, in insertion order. */
    get all() { return this.docs; }
    /** Add one document. addDoc : SearchDoc -> void */
    add(doc) {
        const i = this.docs.length;
        this.docs.push(doc);
        for (const t of trigramsOf(`${doc.title} ${doc.haystack}`.toLowerCase())) {
            const list = this.postings.get(t);
            if (list)
                list.push(i);
            else
                this.postings.set(t, [i]);
        }
    }
    /** Add many documents. addAll : [SearchDoc] -> void */
    addAll(docs) { for (const d of docs)
        this.add(d); }
    /** Remove everything. clear : -> void */
    clear() { this.docs.length = 0; this.postings.clear(); }
    /**
     * Rank documents against a query.
     *
     * search : String {limit, kinds} -> [Match]
     */
    search(query, opts = {}) {
        const limit = opts.limit ?? 40;
        const q = query.trim();
        const kinds = opts.kinds;
        if (q.length === 0) {
            return this.docs
                .filter(d => !kinds || kinds.includes(d.kind))
                .sort((a, b) => b.boost - a.boost)
                .slice(0, limit)
                .map(doc => ({ doc, score: doc.boost, spans: [] }));
        }
        const candidates = this.candidatesFor(q);
        const matches = [];
        for (const i of candidates) {
            const doc = this.docs[i];
            if (kinds && !kinds.includes(doc.kind))
                continue;
            const onTitle = fuzzyMatch(q, doc.title);
            // Matching the haystack (full path, comment body) is worth less than
            // matching the title, so a file named "Roof.PDF" beats a file merely
            // living in a folder called "Roof".
            const onHay = onTitle ? undefined : fuzzyMatch(q, doc.haystack);
            if (!onTitle && !onHay)
                continue;
            const raw = onTitle ? onTitle.score : onHay.score * 0.45;
            matches.push({
                doc,
                score: raw * doc.boost,
                spans: onTitle ? positionsToSpans(onTitle.positions) : [],
            });
        }
        return matches
            .sort((a, b) => b.score - a.score || a.doc.title.length - b.doc.title.length)
            .slice(0, limit);
    }
    /**
     * The document indices worth scoring for a query.
     *
     * For a query of three or more characters we intersect trigram posting lists
     * — cheap and highly selective. If that yields nothing (a fuzzy, non-
     * contiguous query like "ccsup") we fall back to scanning everything, which
     * at a few thousand documents still costs well under a frame.
     * candidatesFor : String -> Iterable<Number>
     */
    candidatesFor(query) {
        if (query.length < MIN_TRIGRAM_QUERY)
            return this.docs.keys();
        const grams = [...new Set(trigramsOf(query.toLowerCase()))];
        if (grams.length === 0)
            return this.docs.keys();
        let acc;
        for (const g of grams) {
            const list = this.postings.get(g);
            if (!list) {
                acc = undefined;
                break;
            }
            if (!acc) {
                acc = new Set(list);
                continue;
            }
            const next = new Set();
            for (const i of list)
                if (acc.has(i))
                    next.add(i);
            acc = next;
            if (acc.size === 0)
                break;
        }
        return acc && acc.size > 0 ? acc : this.docs.keys();
    }
}
/**
 * The three-character sequences of a string, skipping runs of whitespace.
 * trigramsOf : String -> [String]
 */
export function trigramsOf(text) {
    const out = [];
    for (let i = 0; i + 3 <= text.length; i++) {
        const g = text.slice(i, i + 3);
        if (!g.includes(' '))
            out.push(g);
    }
    return out;
}
//# sourceMappingURL=search.js.map